import { useState, useEffect, useCallback } from "react";
import { Receipt, Search, ChevronLeft, ChevronRight, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MobileCards, DataCard, EmptyCard } from "@/components/DataCards";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const KIND_LABEL = { activation: "Ativação", recharge_balance: "Recarga saldo", recharge_credits: "Compra créditos" };
const STATUS = {
  activated: { label: "Ativado", cls: "bg-green-500/15 text-green-600 dark:text-green-400" },
  fulfilled: { label: "Creditado", cls: "bg-green-500/15 text-green-600 dark:text-green-400" },
  paid: { label: "Pago", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400" },
  processing: { label: "Processando", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  pending: { label: "Aguardando", cls: "bg-muted text-muted-foreground" },
};
const brl = (c) => (Number(c || 0) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

export default function AdminOrders() {
  const [data, setData] = useState({ items: [], total: 0, revenue_cents: 0, paid_count: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("all");
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page, limit });
    if (q) params.set("q", q);
    if (status !== "all") params.set("status", status);
    if (kind !== "all") params.set("kind", kind);
    const { data } = await api.get(`/admin/orders?${params}`);
    setData(data);
  }, [q, status, kind, page]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div className="flex items-center gap-3">
        <Receipt className="h-6 w-6 text-primary" />
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Pagamentos</h2>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground"><DollarSign className="h-3.5 w-3.5 text-primary" /> Recebido (Pix)</div>
          <div className="mt-1 font-num text-2xl font-bold text-primary">R$ {brl(data.revenue_cents)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">Pedidos pagos</div>
          <div className="mt-1 font-num text-2xl font-bold">{data.paid_count}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">Total de pedidos</div>
          <div className="mt-1 font-num text-2xl font-bold">{data.total}</div>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar por e-mail, app, MAC ou descrição..." value={q}
            onChange={(e) => { setPage(1); setQ(e.target.value); }} data-testid="orders-search" />
        </div>
        <Select value={kind} onValueChange={(v) => { setPage(1); setKind(v); }}>
          <SelectTrigger className="w-full sm:w-44" data-testid="orders-kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="activation">Ativação</SelectItem>
            <SelectItem value="recharge_balance">Recarga saldo</SelectItem>
            <SelectItem value="recharge_credits">Compra créditos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
          <SelectTrigger className="w-full sm:w-40" data-testid="orders-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="activated">Ativado</SelectItem>
            <SelectItem value="fulfilled">Creditado</SelectItem>
            <SelectItem value="processing">Processando</SelectItem>
            <SelectItem value="pending">Aguardando</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <MobileCards testid="orders-cards">
        {data.items.length === 0 && <EmptyCard>Nenhum pedido encontrado.</EmptyCard>}
        {data.items.map((o) => {
          const st = STATUS[o.status] || { label: o.status, cls: "bg-muted text-muted-foreground" };
          return (
            <DataCard
              key={o.id}
              testid={`order-card-${o.id}`}
              title={o.description || o.app_name || "—"}
              subtitle={o.email || (o.telegram_id ? `TG ${o.telegram_id}` : "—")}
              badge={<span className={`rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>}
              rows={[
                { label: "Valor", value: <span className="font-num font-semibold">R$ {brl(o.amount_cents)}</span> },
                { label: "Tipo", value: KIND_LABEL[o.kind || "activation"] },
                { label: "Origem", value: o.source === "telegram" ? "Telegram" : "Site" },
                { label: "Data", value: new Date(o.created_at).toLocaleString("pt-BR") },
                o.mac ? { label: "MAC", value: <span className="font-mono text-xs">{o.mac}</span>, full: true } : null,
              ]}
            />
          );
        })}
        <div className="flex items-center justify-between rounded-xl border border-border bg-card p-3 text-sm">
          <span className="text-muted-foreground">Página {page} de {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="orders-prev-m"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="orders-next-m"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </MobileCards>

      <div className="hidden rounded-lg border border-border bg-card md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Origem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Nenhum pedido encontrado.</TableCell></TableRow>}
            {data.items.map((o) => {
              const st = STATUS[o.status] || { label: o.status, cls: "bg-muted text-muted-foreground" };
              return (
                <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                  <TableCell className="text-muted-foreground">{new Date(o.created_at).toLocaleString("pt-BR")}</TableCell>
                  <TableCell>{KIND_LABEL[o.kind || "activation"]}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{o.description || o.app_name || "—"}{o.mac ? ` · ${o.mac}` : ""}</TableCell>
                  <TableCell className="text-right font-num font-medium">R$ {brl(o.amount_cents)}</TableCell>
                  <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span></TableCell>
                  <TableCell className="text-muted-foreground">{o.email || (o.telegram_id ? `TG ${o.telegram_id}` : "—")}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{o.source === "telegram" ? "Telegram" : "Site"}</Badge></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between border-t border-border p-3 text-sm">
          <span className="text-muted-foreground">Página {page} de {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="orders-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="orders-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
